/**
 * Camera viewshed: what each camera can actually see.
 *
 * A camera dot tells you a camera is there. It does not tell you the thing you
 * want to know, which is whether you are in front of it — and on a street with
 * buildings on both sides, most of a camera's nominal range is wall. This
 * casts rays from each camera and stops them at the first building they hit,
 * so the shape is the part of the street genuinely in view.
 *
 * Everything here is local geometry. It needs no API, no key and no network:
 * 875 camera positions and 43,071 surveyed footprints are already loaded, and
 * this is what they are worth together.
 *
 * What it does not model, and should not be read as modelling: which way a
 * camera points, its lens, its resolution, or whether it is switched on. OSM
 * tags direction on almost none of them, so this is the *unobstructed* area
 * around a camera, not its field of view. It is an upper bound on what could
 * be seen from that point, which is the honest thing to draw when the aiming
 * is unknown.
 */

import { PolygonLayer } from "@deck.gl/layers";

import { palette } from "../theme/palette.js";
import { DEPTH_TESTED, OVER_EVERYTHING } from "../map/depth.js";

/** How far a street camera can plausibly resolve a person. Generous. */
const RANGE_M = 105;

/** Rays cast per camera. 72 is every 5°, which is smooth at street scale. */
const RAYS = 72;

/** Step along each ray. Small enough not to tunnel through a thin building. */
const STEP_M = 3;

const METRES_PER_DEG_LAT = 110540;
const METRES_PER_DEG_LNG = 85600;   // at Denver's latitude

/** Grid cell for the footprint index, in degrees — comfortably over RANGE_M. */

/**
 * The visible area around one point, as a closed ring.
 *
 * A camera inside a building — mapped on the wall, or slightly misplaced —
 * would otherwise return a degenerate sliver. Those return null instead, so
 * the layer draws nothing rather than a lie.
 */
export function viewshed([lng, lat], index, { range = RANGE_M } = {}) {
  if (index.blocked(lng, lat)) return null;

  const ring = [];
  for (let i = 0; i < RAYS; i += 1) {
    const angle = (i / RAYS) * Math.PI * 2;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);

    let reach = range;
    for (let d = STEP_M; d <= range; d += STEP_M) {
      const px = lng + (dx * d) / METRES_PER_DEG_LNG;
      const py = lat + (dy * d) / METRES_PER_DEG_LAT;
      if (index.blocked(px, py)) {
        // Stop just short of the wall rather than inside it.
        reach = Math.max(0, d - STEP_M);
        break;
      }
    }
    ring.push([
      lng + (dx * reach) / METRES_PER_DEG_LNG,
      lat + (dy * reach) / METRES_PER_DEG_LAT,
    ]);
  }
  ring.push(ring[0]);
  return ring;
}

/**
 * Viewsheds for the cameras currently on screen.
 *
 * Capped deliberately. Computing all 875 at once is several million
 * point-in-polygon tests and would stall the frame; the ones you cannot see
 * are not worth the wait.
 */
export function computeViewsheds(cameras, index, bounds, { limit = 60 } = {}) {
  if (!index || !bounds) return [];
  const [[west, south], [east, north]] = bounds;

  const visible = [];
  for (const camera of cameras ?? []) {
    if (camera.lng < west || camera.lng > east) continue;
    if (camera.lat < south || camera.lat > north) continue;
    visible.push(camera);
    if (visible.length >= limit) break;
  }

  const out = [];
  for (const camera of visible) {
    const ring = viewshed([camera.lng, camera.lat], index);
    if (ring) out.push({ ring, camera });
  }
  return out;
}

/**
 * Where a camera is, above the ground it watches.
 *
 * Three metres is a shop fascia or a low pole — the height most street cameras
 * are actually at, and the height the cone has to hang from for the shape to
 * mean anything.
 */
export const CAMERA_HEIGHT_M = 3;

/**
 * Close enough that the cone is worth building as a volume.
 *
 * Below this you are looking down at a map and a flat wash of colour reads
 * better; at street level a flat wash lies on the ground under your feet and
 * says nothing about what the camera can actually see.
 */
export const VIEWSHED_3D_ZOOM = 17.4;

export function viewshedLayer(sheds, { visible = true, zoom = 0 } = {}) {
  const solid = zoom >= VIEWSHED_3D_ZOOM;
  const c = palette().viewshed;

  return new PolygonLayer({
    id: "viewsheds",
    data: sheds,
    visible,
    getPolygon: (d) => d.ring,

    // Close in, the cone becomes the volume under the camera.
    //
    // Extruded from the ground up to the camera's own height, which is the
    // shape of what a camera on a pole can see: everything below it out to the
    // range, and nothing above it. A flat polygon said the same thing about a
    // rooftop as about the pavement, which at eye level is the one distinction
    // that matters.
    extruded: solid,
    getElevation: CAMERA_HEIGHT_M,

    // Opaque when solid. The pale wash exists so a hundred overlapping cones
    // do not drown the map when you are looking down at the city; standing in
    // one, that same wash is invisible against the ground it is painted on.
    getFillColor: [...c, solid ? 150 : 34],
    getLineColor: [...c, solid ? 220 : 90],
    getLineWidth: 1,
    lineWidthUnits: "pixels",
    stroked: true,
    filled: true,
    pickable: false,
    updateTriggers: {
      getFillColor: solid,
      getLineColor: solid,
      getElevation: solid,
    },
    // Flat, it is a co-planar ground fill and depth writes make it flicker
    // (see landuse.js). Solid, it is a real volume and has to be depth tested
    // or it draws straight through the buildings it is supposed to stop at.
    parameters: solid ? DEPTH_TESTED : OVER_EVERYTHING,
  });
}
